'use client'

import React, { useEffect, useRef, useState } from 'react'
import { cn } from '@/utilities/ui'
import { TYPE_AVOID, type TypeAvoid } from './constants'
import { useInView } from './Reveal'

const SAMPLE = 'Built for\nmore'

function treatment(t: TypeAvoid['treatment']): { box: string; sample: string; size: string } {
  switch (t) {
    case 'caps':
      // Caps are wider, so a slightly smaller size keeps "BUILT FOR / MORE" inside the card.
      return {
        box: 'bg-civo-lime',
        sample: 'text-civo-black uppercase type-replay-caps',
        size: 'text-[34px] sm:text-[40px] lg:text-[68px]',
      }
    case 'tracking':
      return {
        box: 'bg-civo-black',
        sample: 'text-civo-lime tracking-[-0.1em] type-replay-tracking',
        size: 'text-[40px] sm:text-[46px] lg:text-[80px]',
      }
    case 'weight':
      return {
        box: 'bg-civo-black',
        sample: 'text-civo-lime font-bold type-replay-weight',
        size: 'text-[40px] sm:text-[46px] lg:text-[80px]',
      }
    case 'contrast':
      return {
        box: 'bg-civo-cloud',
        sample: 'text-civo-lime type-replay-contrast',
        size: 'text-[40px] sm:text-[46px] lg:text-[80px]',
      }
  }
}

const AvoidCard: React.FC<{ item: TypeAvoid; index: number }> = ({ item, index }) => {
  const { ref, inView } = useInView<HTMLDivElement>(0.3)
  const { box, sample, size } = treatment(item.treatment)

  const [playing, setPlaying] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const play = () => {
    if (timer.current) clearTimeout(timer.current)
    setPlaying(true)
    timer.current = setTimeout(() => setPlaying(false), 1600)
  }
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current) }, [])

  return (
    <div
      ref={ref}
      onClick={play}
      style={{ transitionDelay: inView ? `${Math.min(index, 5) * 90}ms` : '0ms' }}
      className={cn(
        'group flex cursor-pointer flex-col gap-4 transition-[opacity,transform] duration-500 ease-out',
        'motion-reduce:transition-none motion-reduce:opacity-100 motion-reduce:translate-y-0',
        inView ? 'translate-y-0 opacity-100' : 'translate-y-3 opacity-0',
        playing && 'dont-playing',
      )}
    >
      <div
        className={cn(
          'relative flex aspect-[16/10] w-full items-center justify-center overflow-hidden rounded-[16px] px-8 lg:px-12',
          'transition-transform duration-300 ease-out group-hover:-translate-y-1 motion-reduce:transform-none',
          box,
        )}
      >
        <span
          aria-hidden="true"
          className={cn(
            'font-display block whitespace-pre-line text-center font-light leading-[1.0]',
            size,
            sample,
          )}
        >
          {SAMPLE}
        </span>

        <svg
          aria-hidden="true"
          preserveAspectRatio="none"
          className={cn(
            'absolute inset-0 h-full w-full origin-center transition-transform duration-500 ease-out',
            'motion-reduce:transition-none motion-reduce:scale-100',
            inView ? 'scale-100' : 'scale-0',
          )}
          style={{ transitionDelay: inView ? `${Math.min(index, 5) * 90 + 250}ms` : '0ms' }}
        >
          <line
            x1="0"
            y1="100%"
            x2="100%"
            y2="0"
            pathLength={1}
            className="dont-strike stroke-red-500 stroke-[2px] [stroke-dasharray:1] lg:stroke-[3px]"
          />
        </svg>
      </div>

      <p className="body-large text-civo-black">{item.caption}</p>
    </div>
  )
}

export const TypographyDonts: React.FC = () => (
  <div className="grid gap-x-10 gap-y-9 sm:grid-cols-2 lg:gap-x-12 lg:gap-y-14">
    {TYPE_AVOID.map((item, i) => (
      <AvoidCard key={item.treatment} item={item} index={i} />
    ))}
  </div>
)
